import React, { createContext, useState, useEffect, useContext, useCallback } from "react";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth } from "../config/firebase";
import { buildOAuthProvider, isPrivateRelayEmail } from "../utils/oauth";

const AuthContext = createContext({
  user: null,
  userProfile: null,
  loading: true,
  error: null,
  login: async () => {},
  loginWithGoogle: async () => {},
  loginWithApple: async () => {},
  logout: async () => {},
  resetPassword: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Normalize an email for deduping Gmail variants (dots/plus)
  function normalizeEmail(email) {
    try {
      if (!email) return "";
      const lower = email.toLowerCase();
      const [local, domain] = lower.split("@");
      if (domain === "gmail.com" || domain === "googlemail.com") {
        const withoutPlus = local.split("+")[0];
        const withoutDots = withoutPlus.replace(/\./g, "");
        return `${withoutDots}@gmail.com`;
      }
      return `${local}@${domain}`;
    } catch {
      return (email || "").toLowerCase();
    }
  }

  // Load cached data on mount
  useEffect(() => {
    try {
      const cachedUser = localStorage.getItem("muslim_user");
      const cachedProfile = localStorage.getItem("muslim_profile");

      if (cachedUser && cachedProfile) {
        setUser(JSON.parse(cachedUser));
        setUserProfile(JSON.parse(cachedProfile));
      }
    } catch (err) {
      console.error("Error loading cached auth data:", err);
    }
  }, []);

  // Fetches the profile + admin status for a signed-in user and pushes it into
  // state. Split out of the auth listener so callers that change the profile
  // server-side (finishing signup, for example) can pull the fresh copy without
  // waiting for an auth state change that will never come.
  const loadProfile = useCallback(async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Cache basic user data
          const userCache = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            emailVerified: firebaseUser.emailVerified,
          };
          localStorage.setItem("muslim_user", JSON.stringify(userCache));

          // Fetch user profile and admin status via backend APIs
          const API_URL = import.meta.env.VITE_API_URL || "https://us-west1-pdx-msa.cloudfunctions.net/api";
          const idToken = await firebaseUser.getIdToken();

          let profile = {};

          // Fetch user profile from backend
          try {
            const userResponse = await fetch(`${API_URL}/v1/users/me`, {
              headers: { Authorization: `Bearer ${idToken}` },
            });
            if (userResponse.ok) {
              const userData = await userResponse.json();
              profile = userData.user || {};
            }
          } catch (err) {
            console.error("Error fetching user profile from API:", err);
          }

          // Check if user is an admin via backend
          try {
            const adminResponse = await fetch(
              `${API_URL}/check-admin?email=${encodeURIComponent((firebaseUser.email || "").toLowerCase())}`,
              { headers: { Authorization: `Bearer ${idToken}` } }
            );
            if (adminResponse.ok) {
              const adminData = await adminResponse.json();
              if (adminData.isAdmin) {
                // Merge admin data into profile
                profile = {
                  ...profile,
                  isAdmin: true,
                  role: adminData.role || "Admin",
                  masjidId: adminData.masjidId || null,
                  activeMasjidId: adminData.activeMasjidId || null,
                };
              } else {
                // Regular user (not an admin)
                profile = {
                  ...profile,
                  isAdmin: false,
                  role: profile.role || "user",
                };
              }
            }
          } catch (err) {
            console.error("Error checking admin status:", err);
            profile = { ...profile, isAdmin: false, role: profile.role || "user" };
          }

          // Cache profile data
          localStorage.setItem("muslim_profile", JSON.stringify(profile));
          setUserProfile(profile);
        } catch (err) {
          console.error("Error fetching user profile:", err);
          setError(err.message);
        }
      } else {
        // Clear all cache on logout
        localStorage.removeItem("muslim_user");
        localStorage.removeItem("muslim_profile");
        localStorage.removeItem("muslim_server_user");
        setUserProfile(null);
      }

      setLoading(false);
  }, []);

  // Re-reads the current user's profile from the backend. Used after signup
  // completes so the nav stops offering "Complete signup".
  const refreshProfile = useCallback(
    () => loadProfile(auth.currentUser),
    [loadProfile]
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      await loadProfile(firebaseUser);
    });

    return () => unsubscribe();
  }, [loadProfile]);

  const login = async (email, password) => {
    try {
      setError(null);
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Google and Apple both land on the same backend check: the account is
  // identified by its verified email, not by which button was pressed, so a
  // second provider on an existing email resolves to the same user record.
  const signInWithOAuth = async (provider, label) => {
    try {
      setError(null);
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const canonicalEmail = normalizeEmail(user.email || "");

      // Get ID token for backend API calls
      const idToken = await user.getIdToken();

      // Call backend to check for existing user
      const checkResponse = await fetch(
        `${import.meta.env.VITE_API_URL}/v1/users/google-signin-check`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ canonicalEmail }),
        }
      );

      if (!checkResponse.ok) {
        throw new Error("Failed to check existing user");
      }

      const checkData = await checkResponse.json();

      // Return user info for the frontend to handle
      return {
        user,
        needsPhone: checkData.needsPhone,
        existingUser: checkData.existingUser,
        needsSignup: checkData.needsSignup,
        signupComplete: checkData.signupComplete,
        // Only worth mentioning when we are about to make a new account: for
        // someone already signed up under the relay address it is just how they
        // sign in, and saying anything would be noise.
        privateRelay: isPrivateRelayEmail(user.email) && checkData.needsSignup !== false,
      };
    } catch (err) {
      // Log technical errors for debugging but don't expose to users
      console.error(`${label} auth error:`, err);
      setError(err.message);
      throw err;
    }
  };

  const loginWithGoogle = async () =>
    signInWithOAuth(buildOAuthProvider("google"), "Google");

  const loginWithApple = async () =>
    signInWithOAuth(buildOAuthProvider("apple"), "Apple");

  const logout = async () => {
    try {
      setError(null);
      await signOut(auth);
      setUserProfile(null);
      // Clear all cached data
      localStorage.removeItem("muslim_user");
      localStorage.removeItem("muslim_profile");
      localStorage.removeItem("muslim_server_user");
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const completeGoogleOnboarding = async ({
    phone,
    firstName,
    lastName,
    existingUserUid,
  }) => {
    if (!auth.currentUser) {
      throw new Error("Not signed in");
    }

    const idToken = await auth.currentUser.getIdToken();

    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/v1/users/google-signin-complete`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          phone,
          firstName,
          lastName,
          existingUserUid,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || "Failed to complete onboarding");
    }

    const data = await response.json();
    return data;
  };

  const resetPassword = async (email) => {
    try {
      setError(null);

      // Prefer our own backend, which sends the branded email pointing at
      // /auth/action. Firebase's own email is kept as a fallback and must stay:
      // if the endpoint is cold, misdeployed, or unreachable, someone locked
      // out of their account still has to be able to get back in. Both paths
      // mint the same kind of oobCode against the same project, so whichever
      // one runs, the link works.
      const AUTH_SEND_API =
        import.meta.env.VITE_AUTH_SEND_API ||
        "https://us-west1-pdx-msa.cloudfunctions.net/authSend";

      try {
        // Bounded, so a hanging request falls through to Firebase rather than
        // leaving the user watching a spinner. Generous, because aborting a
        // request the server is about to finish sends a second email.
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 20000);
        let response;
        try {
          response = await fetch(`${AUTH_SEND_API}/v1/auth/send-password-reset`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email }),
            signal: controller.signal,
          });
        } finally {
          clearTimeout(timeout);
        }
        if (!response.ok) {
          // A 4xx is the server answering, not the server being down. Falling
          // back would re-send through Firebase the exact request it just
          // refused, defeating the rate limit the 429 exists to enforce, so
          // these are surfaced with a code the login page already maps.
          if (response.status >= 400 && response.status < 500) {
            const data = await response.json().catch(() => ({}));
            const refusal = new Error(data?.error || `authSend responded ${response.status}`);
            refusal.code =
              response.status === 429 ? "auth/too-many-requests" : "auth/invalid-email";
            refusal.deliberate = true;
            throw refusal;
          }
          throw new Error(`authSend responded ${response.status}`);
        }
        return;
      } catch (brandedErr) {
        if (brandedErr?.deliberate) throw brandedErr;
        console.warn(
          "Branded reset email unavailable, falling back to Firebase:",
          brandedErr
        );
        try {
          await sendPasswordResetEmail(auth, email);
        } catch (firebaseErr) {
          // An address with no account has to look exactly like one with an
          // account, or the fallback becomes the enumeration oracle the branded
          // endpoint's generic 200 is careful not to be.
          if (firebaseErr?.code !== "auth/user-not-found") throw firebaseErr;
        }
      }
    } catch (err) {
      // Log technical errors for debugging but don't expose to users
      console.error("Password reset error:", err);
      setError(err.message);
      throw err;
    }
  };

  const value = {
    user,
    userProfile,
    refreshProfile,
    loading,
    error,
    login,
    loginWithGoogle,
    loginWithApple,
    completeGoogleOnboarding,
    logout,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
